import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Alert,
  CircularProgress,
  Divider,
  Checkbox,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Snackbar,
  Tabs,
  Tab,
} from '@mui/material';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import { clubEntryService, ClubEntryScreenSettingsDto, UpdateClubEntrySettingsDto } from '../../../../services/clubEntryService';
import { cardManagementService } from '../../../../services/cardManagementService';
import { useTranslation } from 'react-i18next';
import ClubEntryPackagesManager from './ClubEntryPackagesManager';

interface CardType {
  id: number;
  name: string;
  color: string;
  depositAmount: number;
}

interface DoorConfig {
  [doorId: string]: { name: string; type: string };
}

interface ClubEntrySettingsTabProps {
  screenId: number;
  companyId: string;
  localServiceUrl?: string;
  onSettingsChanged?: (settings: ClubEntryScreenSettingsDto) => void;
}

const DEFAULT_SETTINGS: ClubEntryScreenSettingsDto = {
  quickSaleScreenId: 0,
  isEnabled: false,
  entryMode: 'SINGLE_ENTRY',
  cardMode: 'ONE_PER_RIGHT',
  depositEnabled: false,
  defaultDepositAmount: 0,
  forcePavoPay: false,
  directTriggerEnabled: false,
  accessControlDoorIds: null,
  reservationSystemEnabled: false,
  cardScanModalEnabled: true,
  cardMonitoringPanelEnabled: true,
};

const ClubEntrySettingsTab: React.FC<ClubEntrySettingsTabProps> = ({
  screenId,
  companyId,
  localServiceUrl = 'http://localhost:3003',
  onSettingsChanged,
}) => {
  const { t } = useTranslation('salesSystem');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState<ClubEntryScreenSettingsDto>({
    ...DEFAULT_SETTINGS,
    quickSaleScreenId: screenId,
  });

  const [cardTypes, setCardTypes] = useState<CardType[]>([]);
  const [cardTypesLoading, setCardTypesLoading] = useState(false);
  const [editingDeposit, setEditingDeposit] = useState<{ [id: number]: string }>({});
  const [depositSnackbar, setDepositSnackbar] = useState<string | null>(null);

  const [doors, setDoors] = useState<DoorConfig>({});
  const [doorsLoading, setDoorsLoading] = useState(false);
  const [doorsError, setDoorsError] = useState<string | null>(null);
  const [selectedDoorIds, setSelectedDoorIds] = useState<string[]>([]);

  // İç alt-sekme: 0 = Genel Ayarlar, 1 = Paketler
  const [activeSubTab, setActiveSubTab] = useState(0);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true);
        const data = await clubEntryService.getSettings(screenId);
        // 🐛 DEBUG: Backend yanıtında reservationSystemEnabled var mı?
        console.log('🔵 [ClubEntrySettings] LOAD response:', {
          screenId,
          rawResponseKeys: Object.keys(data || {}),
          reservationSystemEnabled_value: (data as any)?.reservationSystemEnabled,
          reservationSystemEnabled_isUndefined: typeof (data as any)?.reservationSystemEnabled === 'undefined',
          fullData: data,
        });
        const merged = {
          ...DEFAULT_SETTINGS,
          ...data,
          quickSaleScreenId: screenId,
          isEnabled: data.isEnabled ?? false,
          entryMode: data.entryMode || 'SINGLE_ENTRY',
          cardMode: data.cardMode || 'ONE_PER_RIGHT',
          depositEnabled: data.depositEnabled ?? false,
          defaultDepositAmount: data.defaultDepositAmount ?? 0,
          forcePavoPay: data.forcePavoPay ?? false,
          directTriggerEnabled: data.directTriggerEnabled ?? false,
          reservationSystemEnabled: data.reservationSystemEnabled ?? false,
          cardScanModalEnabled: data.cardScanModalEnabled ?? true,
          cardMonitoringPanelEnabled: data.cardMonitoringPanelEnabled ?? true,
        };
        setSettings(merged);
        if (merged.accessControlDoorIds) {
          setSelectedDoorIds(merged.accessControlDoorIds.split(',').filter(Boolean));
        }
      } catch (err: any) {
        if (err?.response?.status !== 404) {
          setError(t('clubEntrySettingsTab.errors.loadFailed'));
        }
      } finally {
        setLoading(false);
      }
    };
    loadSettings();
  }, [screenId]);

  const loadCardTypes = useCallback(async () => {
    if (!companyId) return;
    try {
      setCardTypesLoading(true);
      const response = await cardManagementService.getCardTypes(companyId);
      const data = response.data?.data || response.data || [];
      setCardTypes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('[ClubEntrySettings] Kart tipleri yüklenemedi:', err);
    } finally {
      setCardTypesLoading(false);
    }
  }, [companyId]);

  useEffect(() => { loadCardTypes(); }, [loadCardTypes]);

  const handleDepositBlur = useCallback(async (ct: CardType) => {
    const rawVal = editingDeposit[ct.id];
    if (rawVal === undefined) return;
    const newVal = parseFloat(rawVal);
    if (isNaN(newVal) || newVal === (ct.depositAmount ?? 0)) {
      setEditingDeposit(prev => { const n = { ...prev }; delete n[ct.id]; return n; });
      return;
    }
    try {
      await cardManagementService.updateCardType({ Id: ct.id, DepositAmount: newVal });
      setDepositSnackbar(t('clubEntrySettingsTab.messages.depositUpdated', { cardType: ct.name, amount: newVal.toFixed(2) }));
      loadCardTypes();
    } catch (err) {
      console.error('[ClubEntrySettings] Deposito güncellenemedi:', err);
      setDepositSnackbar(t('clubEntrySettingsTab.errors.depositUpdateFailed'));
    }
    setEditingDeposit(prev => { const n = { ...prev }; delete n[ct.id]; return n; });
  }, [editingDeposit, loadCardTypes]);

  useEffect(() => {
    const loadDoors = async () => {
      try {
        setDoorsLoading(true);
        setDoorsError(null);
        const response = await fetch(`${localServiceUrl}/api/access-control/door-config`, {
          signal: AbortSignal.timeout(5000),
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        if (data.success && data.doorConfig) {
          setDoors(data.doorConfig);
        }
      } catch (err: any) {
        console.error('[ClubEntrySettings] Kapı bilgileri yüklenemedi:', err);
        setDoorsError(t('clubEntrySettingsTab.errors.doorsLoadFailed'));
      } finally {
        setDoorsLoading(false);
      }
    };
    loadDoors();
  }, [localServiceUrl]);

  const handleSave = useCallback(async (updated: ClubEntryScreenSettingsDto) => {
    try {
      setSaving(true);
      setError(null);
      const dto: UpdateClubEntrySettingsDto = {
        isEnabled: updated.isEnabled,
        entryMode: updated.entryMode,
        cardMode: updated.cardMode,
        depositEnabled: updated.depositEnabled,
        defaultDepositAmount: updated.defaultDepositAmount,
        forcePavoPay: updated.forcePavoPay,
        directTriggerEnabled: updated.directTriggerEnabled,
        accessControlDoorIds: updated.accessControlDoorIds,
        reservationSystemEnabled: updated.reservationSystemEnabled ?? false,
        cardScanModalEnabled: updated.cardScanModalEnabled ?? true,
        cardMonitoringPanelEnabled: updated.cardMonitoringPanelEnabled ?? true,
      };
      // 🐛 DEBUG: Save isteği gönderiliyor
      console.log('🟢 [ClubEntrySettings] SAVE isteği:', {
        screenId,
        dto,
        reservationSystemEnabled_send: dto.reservationSystemEnabled,
      });
      const saved = await clubEntryService.saveSettings(screenId, dto);
      // 🐛 DEBUG: Save yanıtı
      console.log('🟢 [ClubEntrySettings] SAVE yanıtı:', {
        rawResponseKeys: Object.keys(saved || {}),
        reservationSystemEnabled_received: (saved as any)?.reservationSystemEnabled,
        reservationSystemEnabled_isUndefined: typeof (saved as any)?.reservationSystemEnabled === 'undefined',
        fullSaved: saved,
      });
      const merged = {
        ...DEFAULT_SETTINGS,
        ...saved,
        quickSaleScreenId: screenId,
        isEnabled: saved.isEnabled ?? updated.isEnabled,
        entryMode: saved.entryMode || updated.entryMode,
        cardMode: saved.cardMode || updated.cardMode,
        depositEnabled: saved.depositEnabled ?? updated.depositEnabled,
        defaultDepositAmount: saved.defaultDepositAmount ?? updated.defaultDepositAmount,
        forcePavoPay: saved.forcePavoPay ?? updated.forcePavoPay,
        directTriggerEnabled: saved.directTriggerEnabled ?? updated.directTriggerEnabled,
        reservationSystemEnabled: saved.reservationSystemEnabled ?? updated.reservationSystemEnabled ?? false,
        cardScanModalEnabled: saved.cardScanModalEnabled ?? updated.cardScanModalEnabled ?? true,
        cardMonitoringPanelEnabled: saved.cardMonitoringPanelEnabled ?? updated.cardMonitoringPanelEnabled ?? true,
      };
      setSettings(merged);
      onSettingsChanged?.(merged);
    } catch (err: any) {
      console.error('[ClubEntrySettings] SAVE ERROR:', err);
      setError(t('clubEntrySettingsTab.errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  }, [screenId, onSettingsChanged]);

  const updateAndSave = useCallback((partial: Partial<ClubEntryScreenSettingsDto>) => {
    setSettings(prev => {
      const updated = { ...prev, ...partial };
      handleSave(updated);
      return updated;
    });
  }, [handleSave]);

  const handleDoorToggle = (doorId: string) => {
    setSelectedDoorIds(prev => {
      const updated = prev.includes(doorId)
        ? prev.filter(id => id !== doorId)
        : [...prev, doorId];
      const doorIdsStr = updated.length > 0 ? updated.join(',') : null;
      updateAndSave({ accessControlDoorIds: doorIdsStr });
      return updated;
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 1 }}>
        <Tabs value={activeSubTab} onChange={(_, v) => setActiveSubTab(v)}>
          <Tab label={t('clubEntrySettingsTab.subTabs.general', 'Genel Ayarlar')} sx={{ textTransform: 'none' }} />
          <Tab label={t('clubEntrySettingsTab.subTabs.packages', 'Paketler')} sx={{ textTransform: 'none' }} />
        </Tabs>
      </Box>

      {activeSubTab === 1 ? (
        <ClubEntryPackagesManager screenId={screenId} companyId={companyId} />
      ) : (
    <Paper elevation={0} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <MeetingRoomIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6">{t('clubEntrySettingsTab.title')}</Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <FormControlLabel
        control={
          <Switch
            checked={settings.isEnabled === true}
            onChange={(e) => updateAndSave({ isEnabled: e.target.checked })}
            disabled={saving}
          />
        }
        label={
          <Typography fontWeight="bold">
            {t('clubEntrySettingsTab.enableModule')}
          </Typography>
        }
        sx={{ mb: 2 }}
      />

      {settings.isEnabled && (
        <>
          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
            {t('clubEntrySettingsTab.entryMode.title')}
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>{t('clubEntrySettingsTab.entryMode.label')}</InputLabel>
            <Select
              value={settings.entryMode || 'SINGLE_ENTRY'}
              label={t('clubEntrySettingsTab.entryMode.label')}
              onChange={(e) => updateAndSave({ entryMode: e.target.value })}
              disabled={saving}
            >
              <MenuItem value="SINGLE_ENTRY">{t('clubEntrySettingsTab.entryMode.options.singleEntry')}</MenuItem>
              <MenuItem value="UNLIMITED_DAILY">{t('clubEntrySettingsTab.entryMode.options.unlimitedDaily')}</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>{t('clubEntrySettingsTab.cardMode.label')}</InputLabel>
            <Select
              value={settings.cardMode || 'ONE_PER_RIGHT'}
              label={t('clubEntrySettingsTab.cardMode.label')}
              onChange={(e) => updateAndSave({ cardMode: e.target.value })}
              disabled={saving}
            >
              <MenuItem value="ONE_PER_RIGHT">{t('clubEntrySettingsTab.cardMode.options.onePerRight')}</MenuItem>
              <MenuItem value="FLEXIBLE">{t('clubEntrySettingsTab.cardMode.options.flexible')}</MenuItem>
            </Select>
          </FormControl>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
            {t('clubEntrySettingsTab.cardScan.title', 'Kart Verme / Okutma')}
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={settings.cardScanModalEnabled !== false}
                onChange={(e) => updateAndSave({ cardScanModalEnabled: e.target.checked })}
                disabled={saving}
              />
            }
            label={t('clubEntrySettingsTab.cardScan.enable', 'Ödeme anında kart okutma modalını göster')}
            sx={{ mb: 1 }}
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            {t('clubEntrySettingsTab.cardScan.hint', 'Kapalı iken Nakit/POS butonuna basıldığında kart okutma modalı gösterilmez, depozito alınmaz ve işlem doğrudan normal hızlı satış gibi tamamlanır (müşteriye kart verilmeyen işletmeler için).')}
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
            {t('clubEntrySettingsTab.deposit.title')}
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={settings.depositEnabled === true}
                onChange={(e) => updateAndSave({ depositEnabled: e.target.checked })}
                disabled={saving}
              />
            }
            label={t('clubEntrySettingsTab.deposit.enable')}
            sx={{ mb: 1 }}
          />

          {settings.depositEnabled && (
            <Box sx={{ mt: 1, mb: 2 }}>
              {cardTypesLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">{t('clubEntrySettingsTab.deposit.loadingCardTypes')}</Typography>
                </Box>
              ) : cardTypes.length === 0 ? (
                <Alert severity="info" sx={{ mb: 1 }}>
                  {t('clubEntrySettingsTab.deposit.noCardTypes')}
                </Alert>
              ) : (
                <>
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('clubEntrySettingsTab.deposit.table.cardType')}</TableCell>
                          <TableCell align="right">{t('clubEntrySettingsTab.deposit.table.depositAmount')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {cardTypes.map((ct) => (
                          <TableRow key={ct.id}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{
                                  width: 14, height: 14, borderRadius: '50%',
                                  bgcolor: ct.color || '#ccc', flexShrink: 0
                                }} />
                                <Typography variant="body2">{ct.name}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <TextField
                                size="small"
                                type="number"
                                value={editingDeposit[ct.id] !== undefined ? editingDeposit[ct.id] : (ct.depositAmount ?? 0)}
                                onChange={(e) => setEditingDeposit(prev => ({ ...prev, [ct.id]: e.target.value }))}
                                onBlur={() => handleDepositBlur(ct)}
                                onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                                sx={{ width: 110 }}
                                inputProps={{ style: { textAlign: 'right' }, min: 0, step: '0.01' }}
                                InputProps={{ startAdornment: <Typography variant="body2" sx={{ mr: 0.5 }}>₺</Typography> }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Typography variant="caption" color="text.secondary">
                    {t('clubEntrySettingsTab.deposit.hint')}
                  </Typography>
                </>
              )}
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
            {t('clubEntrySettingsTab.paymentDevice.title')}
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={settings.forcePavoPay === true}
                onChange={(e) => updateAndSave({ forcePavoPay: e.target.checked })}
                disabled={saving}
              />
            }
            label={t('clubEntrySettingsTab.paymentDevice.forcePavoPay')}
            sx={{ mb: 1 }}
          />

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
            {t('clubEntrySettingsTab.accessControl.title')}
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={settings.directTriggerEnabled === true}
                onChange={(e) => updateAndSave({ directTriggerEnabled: e.target.checked })}
                disabled={saving}
              />
            }
            label={t('clubEntrySettingsTab.accessControl.directTrigger')}
            sx={{ mb: 1 }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={settings.cardMonitoringPanelEnabled !== false}
                onChange={(e) => updateAndSave({ cardMonitoringPanelEnabled: e.target.checked })}
                disabled={saving}
              />
            }
            label={t('clubEntrySettingsTab.accessControl.cardMonitoringPanel', 'Canlı kart izleme panelini göster')}
            sx={{ mb: 1 }}
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            {t('clubEntrySettingsTab.accessControl.cardMonitoringPanelHint', 'Kulüp girişi ekranının sağındaki canlı kart geçiş izleme panelini gösterir veya gizler.')}
          </Typography>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t('clubEntrySettingsTab.accessControl.doorsToControl')}
          </Typography>

          {doorsLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">{t('clubEntrySettingsTab.accessControl.loadingDoors')}</Typography>
            </Box>
          ) : doorsError ? (
            <Alert severity="warning" sx={{ mb: 1 }}>
              {doorsError}
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                {t('clubEntrySettingsTab.accessControl.localServiceHint', { localServiceUrl })}
              </Typography>
            </Alert>
          ) : Object.keys(doors).length === 0 ? (
            <Alert severity="info" sx={{ mb: 1 }}>
              {t('clubEntrySettingsTab.accessControl.noDoors')}
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1 }}>
              {Object.entries(doors).map(([doorId, doorInfo]) => (
                <FormControlLabel
                  key={doorId}
                  control={
                    <Checkbox
                      checked={selectedDoorIds.includes(doorId)}
                      onChange={() => handleDoorToggle(doorId)}
                      disabled={saving}
                      size="small"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">
                        {t('clubEntrySettingsTab.accessControl.doorWithName', {
                          doorId,
                          doorName: doorInfo.name || t('clubEntrySettingsTab.accessControl.doorFallback', { doorId }),
                        })}
                      </Typography>
                      <Chip
                        label={
                          doorInfo.type === 'entry'
                            ? t('quickSaleScreen.accessControlLivePanel.direction.entry')
                            : doorInfo.type === 'exit'
                              ? t('quickSaleScreen.accessControlLivePanel.direction.exit')
                              : doorInfo.type
                        }
                        size="small"
                        color={doorInfo.type === 'entry' ? 'success' : 'default'}
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    </Box>
                  }
                />
              ))}
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
            {t('clubEntrySettingsTab.reservationSystem.title')}
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={settings.reservationSystemEnabled === true}
                onChange={(e) => updateAndSave({ reservationSystemEnabled: e.target.checked })}
                disabled={saving}
              />
            }
            label={t('clubEntrySettingsTab.reservationSystem.enable')}
            sx={{ mb: 1 }}
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            {t('clubEntrySettingsTab.reservationSystem.hint')}
          </Typography>
        </>
      )}

      {saving && (
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, gap: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="caption" color="text.secondary">
            {t('quickSaleSettingsModal.footer.saving')}
          </Typography>
        </Box>
      )}

      <Snackbar
        open={!!depositSnackbar}
        autoHideDuration={3000}
        onClose={() => setDepositSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setDepositSnackbar(null)} severity="success" variant="filled" sx={{ width: '100%' }}>
          {depositSnackbar}
        </Alert>
      </Snackbar>
    </Paper>
      )}
    </Box>
  );
};

export default ClubEntrySettingsTab;
